import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Button, CreateButton } from '../../../ui/Buttons';
import { Input } from '../../../ui/Inputs';
import { DeleteIcon } from '../../../ui/Icons';
import { Loading } from '../../../ui/Loading';
import { Notification } from '../../../ui/Modals';
import { Title } from '../../../ui/Title';
import { useCrmTenantsEditor } from './hooks/useCrmTenantsEditor';
import s from './CrmTenantsEditor.module.scss';

const CrmTenantsEditor: React.FC = () => {
  const { t } = useTranslation();
  const {
    rows,
    rowFieldErrors,
    validationError,
    saveError,
    showLoading,
    hasRows,
    hasChanges,
    isLoading,
    handleChangeRow,
    handleDeleteRow,
    handleAddRow,
    handleCancel,
    handleSave,
    handleCloseSaveError
  } = useCrmTenantsEditor();

  return (
    <div className={s.editor}>
      <Title title={t('settingsPage.crmTenants.title')} variant="h2" className={s.title} />

      {hasRows ? (
        <div className={s.table}>
          <div className={s.tableHeader}>
            <span>{t('settingsPage.crmTenants.key')}</span>
            <span>{t('settingsPage.crmTenants.value')}</span>
          </div>

          <div className={s.tableRows}>
            {rows.map((row) => (
              <div className={s.tableRow} key={row.id}>
                <Input
                  className={s.input}
                  name={`tenant-key-${row.id}`}
                  value={row.key}
                  error={rowFieldErrors[row.id]?.key}
                  placeholder={`${t('settingsPage.crmTenants.keyPlaceholder')}`}
                  onChange={(event) => handleChangeRow(row.id, 'key', event.target.value)}
                />
                <Input
                  className={s.input}
                  name={`tenant-value-${row.id}`}
                  value={row.value}
                  error={rowFieldErrors[row.id]?.value}
                  placeholder={`${t('settingsPage.crmTenants.valuePlaceholder')}`}
                  onChange={(event) => handleChangeRow(row.id, 'value', event.target.value)}
                />
                <button
                  type="button"
                  className={s.deleteBtn}
                  aria-label={`${t('buttons.delete')}`}
                  onClick={() => handleDeleteRow(row.id)}
                >
                  <DeleteIcon className={s.deleteIcon} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        !isLoading && <p className={cn('empty', s.empty)}>{t('settingsPage.crmTenants.empty')}</p>
      )}

      <CreateButton className={s.addBtn} onClick={handleAddRow}>
        {t('settingsPage.crmTenants.addTenant')}
      </CreateButton>

      {validationError && <p className={s.error}>{validationError}</p>}

      <div className={s.actions}>
        <Button onClick={handleSave} disabled={!hasChanges || showLoading}>
          {t('buttons.save')}
        </Button>
        <Button red onClick={handleCancel} disabled={!hasChanges || showLoading}>
          {t('buttons.cancel')}
        </Button>
      </div>

      {showLoading && <Loading absolute />}

      <Notification
        title={t('settingsPage.saveError')}
        open={saveError}
        onClose={handleCloseSaveError}
        red
        withExclamationIcon
      />
    </div>
  );
};

export default CrmTenantsEditor;
